import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function EarningsAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="Trend - Earnings API" description="Query trend data via the Earnings API; request parameters, code examples and the response schema for the trend endpoint."  canonicalUrl="https://axionquant.com/docs/03-fundamentals/earnings/03-trend" />
      <DocHeading className="text-3xl font-bold text-gray-900 mb-4">Trend - Earnings API</DocHeading>

      <div className="space-y-12" ref={sectionRef}>
        {/* ========== 2. Earnings Trend ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /earnings/:ticker/trend
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Provides forward-looking earnings estimates and analyst sentiment for upcoming quarters.
                Includes EPS and revenue estimates (average, low, high), year-ago comparisons,
                recent estimate revisions, and EPS trend over the last 90 days.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"> <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                        Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/earnings/AAPL/trend', '/earnings/MSFT/trend']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">period</p>
                  <p className="text-sm text-gray-600">Fiscal period (&quot;0q&quot; = current quarter, &quot;+1q&quot; = next quarter, &quot;0y&quot; = current fiscal year, &quot;LTG&quot; = long-term growth)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">endDate</p>
                  <p className="text-sm text-gray-600">Fiscal period end date</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">growth</p>
                  <p className="text-sm text-gray-600">Estimated earnings growth rate (decimal)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">earningsEstimate</p>
                  <p className="text-sm text-gray-600">Object containing EPS estimate details: avg, low, high, yearAgoEps, numberOfAnalysts, growth</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">revenueEstimate</p>
                  <p className="text-sm text-gray-600">Object containing revenue estimate details (similar structure)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">epsTrend</p>
                  <p className="text-sm text-gray-600">EPS estimates over time (current, 7d ago, 30d ago, 60d ago, 90d ago)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">epsRevisions</p>
                  <p className="text-sm text-gray-600">Count of upward/downward revisions in the last 7/30 days</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Earnings Trend (Apple)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Earnings Trend (Apple)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="earnings/ticker/trend"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response (abbreviated)</p>
                <Code id="03-fundamentals/earnings/trend-1"></Code>
              </div>
            </div>
          </div>
        </div>

        {/* ========== 3. Index Trend ========== */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /earnings/:ticker/index
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Returns earnings growth estimates for the S&amp;P 500 index (or other benchmark)
                for comparison against the company&apos;s own estimates. Useful for contextualizing
                company performance within the broader market.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Note: The <code className="text-xs">:ticker</code> parameter is required but currently the endpoint
                always returns data for the S&amp;P 500 (symbol &quot;SP5&quot;). Future versions may support other indices.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm"><span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">Required</span></td>
                      <td className="px-4 py-3 text-sm text-gray-700">Stock ticker symbol (used for context)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/earnings/AAPL/index', '/earnings/MSFT/index']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Key Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">symbol</p>
                  <p className="text-sm text-gray-600">Index symbol (currently &quot;SP5&quot; for S&amp;P 500)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">estimates[].period</p>
                  <p className="text-sm text-gray-600">Period code: &quot;0q&quot; (current quarter), &quot;+1q&quot; (next quarter), &quot;0y&quot; (current year), &quot;+1y&quot; (next year), &quot;LTG&quot; (long-term growth)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">estimates[].growth</p>
                  <p className="text-sm text-gray-600">Estimated earnings growth rate for the index (decimal)</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="Index Earnings Trend (S&amp;P 500)" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">Index Earnings Trend (S&amp;P 500)</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="earnings/ticker/index"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="03-fundamentals/earnings/trend-2"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
